import { NgModule } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropzoneModule } from 'ngx-dropzone-wrapper';
import { NgbDateParserFormatter, NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgBusyModule } from 'ng-busy';
import { OverlayscrollbarsModule } from 'overlayscrollbars-ngx';

import { NgCakeItEasyUtilsModule } from '@cakeiteasy/web-utils';
import { ImageCropperExtraModule } from '../image-cropper/image-cropper-extra.module';

import { UiSelectComponent } from './components/ui-select/ui-select.component';
import { UiMessageComponent } from './components/ui-message/ui-message.component';
import { UiMessageCustomComponent } from './components/ui-message-custom/ui-message-custom.component';
import { UiSearchBarComponent } from './components/ui-search-bar/ui-search-bar.component';
import { UiI18nPhoneInputComponent } from './components/ui-i18n-phone-input/ui-i18n-phone-input.component';
import { UiCheckboxComponent } from './components/ui-checkbox/ui-checkbox.component';
import { UiRadioGroupComponent } from './components/ui-radio-group/ui-radio-group.component';
import { UiCheckboxBtnComponent } from './components/ui-checkbox-btn/ui-checkbox-btn.component';
import { UiExpandInputComponent } from './components/ui-expand-input/ui-expand-input.component';
import { UiMultiSelectComponent } from './components/ui-multi-select/ui-multi-select.component';
import { UiCakeCounterComponent } from './components/ui-cake-counter/ui-cake-counter.component';
import { UiBtnHintComponent } from './components/ui-btn-hint/ui-btn-hint.component';
import { UiBtnTwoLinesComponent } from './components/ui-btn-two-lines/ui-btn-two-lines.component';
import { UiExtraProductsCheckboxComponent } from './components/ui-extra-products-checkbox/ui-extra-products-checkbox.component';
import { UiExtraProductsRadioComponent } from './components/ui-extra-products-radio/ui-extra-products-radio.component';
import { UiExpandableTextareaComponent } from './components/ui-expandable-textarea/ui-expandable-textarea.component';
import { UiDatepickerPopupComponent } from './components/ui-datepicker/ui-datepicker-popup.component';
import { UiOrderLineStatusBadgeComponent } from './components/ui-order-line-status-badge/ui-order-line-status-badge.component';

import { UiBackButtonComponent } from './components/ui-back-button/ui-back-button.component';
import { UiBackButtonWrapperComponent } from './components/ui-back-button-wrapper/ui-back-button-wrapper.component';
import { UiCustomToasterComponent } from './components/ui-custom-toaster/ui-custom-toaster.component';
import { UiInputWithTooltipComponent } from './components/ui-input-with-tooltip/ui-input-with-tooltip.component';
import { UiOutletsMapComponent } from './components/ui-outlets-map/ui-outlets-map.component';
import { UiTextSliceComponent } from './components/ui-text-slice/ui-text-slice.component';

import { InputMaskDirective } from './directives/input-mask.directive';
import { ResizeDirective } from './directives/resize.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ImagePreviewTriggerDirective } from './directives/image-preview-trigger.directive';
import { NgbDateParserFormatterService } from './services/ngb-date-parser-formatter.service';
import { NgbDatepickerI18nService } from './services/ngb-datepicker-i18n.service';
import { UiNoBrAfterComponent } from './components/ui-no-br-after/ui-no-br-after.component';
import { UiGifLoaderComponent } from './components/ui-gif-loader/ui-gif-loader.component';
import { UiScrollToCompleteComponent } from './components/ui-scroll-to-complete/ui-scroll-to-complete.component';
import { UiInputLoaderWrapComponent } from './components/ui-input-loader-wrap/ui-input-loader-wrap.component';
import { UiSinglePiecesCounterComponent } from './components/ui-cake-counter/ui-single-pieces-counter.component';
import { UiRadioItemComponent } from './components/ui-radio-item/ui-radio-item.component';

import { UiCakeBuildVariantsComponent } from './components/ui-cake-build-variants/ui-cake-build-variants.component';
import { UiExtraProductPopupPictureComponent } from './components/ui-extra-product-popup-picture/ui-extra-product-popup-picture.component';
import { UiCakeBuildSizeComponent } from './components/ui-cake-build-size/ui-cake-build-size.component';
import { UiCakeBuildSizesComponent } from './components/ui-cake-build-sizes/ui-cake-build-sizes.component';
import { UiCakeBuildExtraProductsComponent } from './components/ui-cake-build-extra-products/ui-cake-build-extra-products.component';
import { UiCakeBuildQuestionComponent } from './components/ui-cake-build-question/ui-cake-build-question.component';
import { UiCakeBuildTextComponent } from './components/ui-cake-build-text/ui-cake-build-text.component';
import { UiCakeBuildGreetingCardComponent } from './components/ui-cake-build-greeting-card/ui-cake-build-greeting-card.component';
import { UiCakeBuildMotiveComponent } from './components/ui-cake-build-motive/ui-cake-build-motive.component';
import { UiChooseMotiveSectionComponent } from './components/ui-cake-build-motive/ui-choose-motive-section/ui-choose-motive-section.component';
import { UiChooseMotiveModalComponent } from './components/ui-cake-build-motive/ui-choose-motive-modal/ui-choose-motive-modal.component';
import {
  UiCropImageAdvancedModalComponent
} from './components/ui-cake-build-motive/ui-choose-motive-with-guide-modal/ui-crop-image-advanced-modal.component';
import { UiOutletsAccordionComponent } from './components/ui-outlets-accordion/ui-outlets-accordion.component';
import { UiOutletsMapMobileComponent } from './components/ui-outlets-map-mobile/ui-outlets-map-mobile.component';
import { UiAccordionOutletMapComponent } from './components/ui-outlets-accordion/ui-accordion-outlet-map/ui-accordion-outlet-map.component';
import { UiMapWithOutletsAccordionComponent } from './components/ui-map-with-outlets-accordion/ui-map-with-outlets-accordion.component';
import { UiMapForOutletsAccordionComponent } from './components/ui-map-with-outlets-accordion/ui-map-for-outlets-accordion/ui-map-for-outlets-accordion.component';
import { UiInvoicePostAddressComponent } from './components/ui-post-address/ui-invoice-post-address.component';
import { UiChooseMotiveCropperComponent } from './components/ui-cake-build-motive/ui-choose-motive-cropper/ui-choose-motive-cropper.component';
import { UiNewsletterSignupComponent } from './components/ui-newsletter-signup/ui-newsletter-signup.component';
import { UiSearchProductsSceletonComponent } from './components/ui-search-products-sceleton/ui-search-products-sceleton.component';
import { UiShapeIconComponent } from './components/ui-shape-icon/ui-shape-icon.component';
import { UiSizeShapeInfoComponent } from './components/ui-size-shape-info/ui-size-shape-info.component';
import { UiFullSizeImageComponent } from './modals/ui-full-size-image/ui-full-size-image.component';
import { UiDiscountBadgeComponent } from './components/ui-discount-badge/ui-discount-badge.component';
import { UiBtnCircleComponent } from './components/ui-btn-circle/ui-btn-circle.component';
import { UiEmailMisspelledComponent } from './components/ui-email-misspelled/ui-email-misspelled.component';
import { UiDeadlinesInfoComponent } from './components/ui-deadlines-info/ui-deadlines-info.component';
import { UiActionResultComponent } from './components/ui-action-result/ui-action-result.component';
import { UiDaysLeftCountComponent } from './components/ui-days-before-delivery/ui-days-left-count/ui-days-left-count.component';
import { UiInDaysCountComponent } from './components/ui-days-before-delivery/ui-in-days-count/ui-in-days-count.component';
import { UiOrderDeliveryTimeComponent } from './components/ui-order-delivery-time/ui-order-delivery-time.component';
import { UiChatComponent } from './components/ui-chat/ui-chat.component';
import { UiDeliveryTypeIconComponent } from './components/ui-delivery-type-icon/ui-delivery-type-icon.component';
import { UiMultipleVariantsCounterComponent } from './components/ui-cake-counter/ui-multiple-variants-counter.component';
import { UiDetailsPinnedMsgComponent } from './components/ui-details-pinned-msg/ui-details-pinned-msg.component';
import { ImagesMsgComponent } from './components/ui-details-pinned-msg/images-msg/images-msg.component';
import { ChoicesMsgComponent } from './components/ui-details-pinned-msg/choices-msg/choices-msg.component';
import { AnswerMsgComponent } from './components/ui-details-pinned-msg/answer-msg/answer-msg.component';
import { UiOrderRequestPinnedMessageOldVersionComponent } from './components/ui-order-request-pinned-message-old-version/ui-order-request-pinned-message-old-version.component';
import { UiTypeaheadComponent } from './components/ui-typeahead/ui-typeahead.component';
import { UiTypeaheadAddressComponent } from './components/ui-typeahead-address/ui-typeahead-address.component';
import { UiDropdownComponent } from './components/ui-dropdown/ui-dropdown.component';
import { UiDeliveryDatepickerComponent } from './components/ui-delivery-datepicker/ui-delivery-datepicker.component';
import { UiDatetimeSkeletonComponent } from './components/ui-datetime-skeleton/ui-datetime-skeleton.component';
import { StoresMapModalComponent } from './modals/stores-map-modal/stores-map-modal.component';
import { UiStoresMapBtnComponent } from './components/ui-stores-map-btn/ui-stores-map-btn.component';

const COMPONENTS = [
  UiSelectComponent,
  UiMessageComponent,
  UiMessageCustomComponent,
  UiCheckboxComponent,
  UiRadioGroupComponent,
  UiBtnHintComponent,
  UiBtnTwoLinesComponent,
  UiCheckboxBtnComponent,
  UiExpandInputComponent,
  UiSearchBarComponent,
  UiI18nPhoneInputComponent,
  UiMultiSelectComponent,
  UiCakeCounterComponent,
  UiSinglePiecesCounterComponent,
  UiMultipleVariantsCounterComponent,
  UiExtraProductsCheckboxComponent,
  UiExtraProductsRadioComponent,
  UiDatepickerPopupComponent,
  UiExpandableTextareaComponent,
  UiOrderLineStatusBadgeComponent,
  UiActionResultComponent,

  UiBackButtonComponent,
  UiBackButtonWrapperComponent,
  UiCustomToasterComponent,
  UiInputWithTooltipComponent,
  UiOutletsMapComponent,
  UiTextSliceComponent,
  UiNoBrAfterComponent,
  UiInputLoaderWrapComponent,
  UiRadioItemComponent,
  UiExtraProductPopupPictureComponent,

  InputMaskDirective,
  ResizeDirective,
  ClickOutsideDirective,
  ImagePreviewTriggerDirective,
  UiScrollToCompleteComponent,
  UiGifLoaderComponent,

  UiCakeBuildMotiveComponent,
  UiChooseMotiveSectionComponent,
  UiChooseMotiveModalComponent,
  UiCropImageAdvancedModalComponent,
  UiCakeBuildSizeComponent,
  UiCakeBuildSizesComponent,
  UiCakeBuildVariantsComponent,
  UiCakeBuildExtraProductsComponent,
  UiCakeBuildQuestionComponent,
  UiCakeBuildTextComponent,
  UiCakeBuildGreetingCardComponent,

  UiMapWithOutletsAccordionComponent,
  UiInvoicePostAddressComponent,
  UiChooseMotiveCropperComponent,
  UiNewsletterSignupComponent,
  UiSearchProductsSceletonComponent,
  UiShapeIconComponent,
  UiSizeShapeInfoComponent,
  UiBtnCircleComponent,
  UiEmailMisspelledComponent,
  UiDeadlinesInfoComponent,

  UiDaysLeftCountComponent,
  UiInDaysCountComponent,
  UiOrderDeliveryTimeComponent,
  UiDetailsPinnedMsgComponent,
  UiOrderRequestPinnedMessageOldVersionComponent,
  UiChatComponent,
  UiDeliveryTypeIconComponent,
  UiTypeaheadComponent,
  UiTypeaheadAddressComponent,
  UiDropdownComponent,
  UiDeliveryDatepickerComponent,
  UiDatetimeSkeletonComponent,

  UiFullSizeImageComponent,
  UiStoresMapBtnComponent,
  StoresMapModalComponent,
];

@NgModule({
  imports: [
    NgbModule,
    FormsModule,
    CommonModule,
    DropzoneModule,
    TranslateModule,
    NgCakeItEasyUtilsModule,
    ImageCropperExtraModule.forRoot(),
    ReactiveFormsModule,
    NgBusyModule,
    OverlayscrollbarsModule,
    UiDiscountBadgeComponent,
    UiAccordionOutletMapComponent,
    UiMapForOutletsAccordionComponent,
    UiOutletsAccordionComponent,
    UiOutletsMapMobileComponent,
  ],
  declarations: [
    ...COMPONENTS,

    ImagesMsgComponent,
    ChoicesMsgComponent,
    AnswerMsgComponent,
  ],
  exports: [...COMPONENTS, UiDiscountBadgeComponent, UiOutletsAccordionComponent, UiOutletsMapMobileComponent],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateParserFormatterService },
    { provide: NgbDatepickerI18n, useClass: NgbDatepickerI18nService },
  ],
})
export class CakeItEasyWebUiModule {}
